import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { ImageOff, MessageCircle, Send, FileDown, ExternalLink } from "lucide-react";
import { CONTACT } from "../lib/constants";
import { downloadDataUrl, openDataUrlInNewTab } from "../lib/pdf";
import { toast } from "sonner";

export default function ProductDetailModal({ product, onClose, onCotizar }) {
  if (!product) return null;
  const waMsg = encodeURIComponent(
    `Hola Pino Verde, me interesa información sobre el producto: ${product.name}${
      product.sku ? ` (SKU: ${product.sku})` : ""
    }.`
  );
  const waUrl = `https://wa.me/${CONTACT.whatsappRaw}?text=${waMsg}`;

  const fileNameFor = () =>
    product.tech_sheet_filename ||
    `ficha-tecnica-${(product.sku || product.name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")}.pdf`;

  const onDownloadPdf = () => {
    if (!product.tech_sheet_pdf) return;
    const ok = downloadDataUrl(product.tech_sheet_pdf, fileNameFor());
    if (!ok) {
      toast.error("No se pudo descargar", {
        description: "Probá con 'Abrir en pestaña nueva'.",
      });
    } else {
      toast.success("Descargando ficha técnica...");
    }
  };

  const onOpenPdf = () => {
    if (!product.tech_sheet_pdf) return;
    const ok = openDataUrlInNewTab(product.tech_sheet_pdf);
    if (!ok) {
      toast.error(
        "Tu navegador bloqueó la apertura. Permití pop-ups o usá el botón de descarga."
      );
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto rounded-sm p-0"
        data-testid={`product-modal-${product.id}`}
      >
        <div className="grid lg:grid-cols-2">
          {/* Image */}
          <div className="bg-slate-50 aspect-square lg:aspect-auto flex items-center justify-center p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-slate-200">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <div className="text-slate-300 flex flex-col items-center">
                <ImageOff size={48} />
                <span className="text-sm mt-3">Sin foto disponible</span>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="p-6 lg:p-8">
            <DialogHeader className="space-y-2 mb-5 text-left">
              {product.brand && (
                <div className="overline text-pv-orange">{product.brand}</div>
              )}
              <DialogTitle className="font-display font-extrabold text-pv-green text-2xl lg:text-3xl tracking-tight leading-tight">
                {product.name}
              </DialogTitle>
              {product.sku && (
                <div className="text-xs text-slate-400">SKU: {product.sku}</div>
              )}
            </DialogHeader>

            {product.short_description && (
              <p className="text-slate-700 mb-5 leading-relaxed">
                {product.short_description}
              </p>
            )}

            {product.description && (
              <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                {product.description}
              </p>
            )}

            {product.specs && product.specs.length > 0 && (
              <div className="mb-6">
                <div className="overline text-pv-green mb-3">
                  Ficha técnica
                </div>
                <div className="border-t border-slate-200">
                  {product.specs.map((s, i) => (
                    <div
                      key={i}
                      data-testid={`spec-row-${i}`}
                      className="grid grid-cols-3 gap-3 py-2.5 border-b border-slate-100 text-sm"
                    >
                      <div className="text-slate-500 font-medium col-span-1">
                        {s.label}
                      </div>
                      <div className="text-slate-800 col-span-2">
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                onClick={() => onCotizar(product)}
                data-testid="modal-cotizar-form"
                className="rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white h-12 px-6 font-semibold flex-1"
              >
                <Send size={16} className="mr-2" />
                Solicitar cotización
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="modal-cotizar-whatsapp"
                className="rounded-sm border-pv-green text-pv-green hover:bg-pv-green hover:text-white h-12 px-6 font-semibold"
              >
                <a href={waUrl} target="_blank" rel="noopener noreferrer">
                  <MessageCircle size={16} className="mr-2" />
                  WhatsApp
                </a>
              </Button>
            </div>

            {product.tech_sheet_pdf && (
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                <Button
                  type="button"
                  onClick={onDownloadPdf}
                  variant="outline"
                  data-testid="modal-download-pdf"
                  className="rounded-sm border-slate-300 text-slate-700 hover:bg-slate-100 h-11 font-semibold"
                >
                  <FileDown size={16} className="mr-2 text-pv-orange" />
                  Descargar ficha PDF
                </Button>
                <Button
                  type="button"
                  onClick={onOpenPdf}
                  variant="outline"
                  data-testid="modal-open-pdf"
                  className="rounded-sm border-slate-300 text-slate-700 hover:bg-slate-100 h-11 font-semibold"
                >
                  <ExternalLink size={16} className="mr-2 text-pv-green" />
                  Abrir en pestaña nueva
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
